import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import Logo from './Logo';
import SEO from './SEO';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <SEO
        title="Privacy Policy"
        description="Privacy Policy for GA4.so — Learn how we collect, use, and protect your personal data when using our AI-powered Google Analytics 4 reporting agent."
        path="/privacy-policy"
      />
      {/* Header - Same as landing page */}
      <header className="border-b border-border/80">
        <div className="container-custom mx-auto px-4 md:px-6 py-6 flex items-center justify-between">
          <Logo />
          <nav className="hidden md:flex items-center gap-6 text-sm text-brand-gray-dark">
            <a href="/#features" className="hover:text-primary transition-colors">Features</a>
            <a href="/#how" className="hover:text-primary transition-colors">How it works</a>
            <a href="/#pricing" className="hover:text-primary transition-colors">Pricing</a>
          </nav>
          <div className="flex items-center gap-3">
            <a href="/login" className="px-4 py-2 rounded-lg border border-border text-brand-gray-dark hover:border-brand-gray-medium transition-colors">Login</a>
            <a href="/signup" className="px-4 py-2 rounded-lg bg-primary text-white hover:opacity-90 flex items-center gap-2 transition-all hover:shadow-lg hover:shadow-primary/20">
              Try the Agent <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <div className="container-custom mx-auto px-4 md:px-6 py-16">
          <article className="font-sans max-w-4xl">
            <div className="mb-12 border-b border-border/80 pb-8">
              <h1 className="text-5xl font-bold mb-2 text-brand-gray-dark font-inter">Privacy Policy</h1>
              <p className="text-lg text-brand-gray-medium font-semibold">of www.ga4.so</p>
            </div>

            <p className="text-lg text-brand-gray-dark mb-6 leading-relaxed">
              This Application collects some Personal Data from its Users.
            </p>

            <div className="border-l-4 border-primary/20 p-6 mb-12">
              <p className="text-brand-gray-dark">
                <strong>Note:</strong> This document contains a section dedicated to Users in the United States and their privacy rights.
              </p>
            </div>

            <p className="text-sm text-brand-gray-medium mb-12 italic">
              This document can be printed for reference by using the print command in the settings of any browser.
            </p>

            <section className="mb-12">
              <h2 className="text-3xl font-bold mb-6 text-brand-gray-dark font-inter border-b border-border/50 pb-3">Owner and Data Controller</h2>
              <div className="p-8 border border-border/20">
                <p className="text-brand-gray-dark font-inter font-semibold text-lg mb-4">Gigaparsec DataLab GmbH</p>
                <p className="text-sm text-brand-gray-dark mb-4">Operating GA4.so</p>
                <div className="mt-6 pt-6 border-t border-border/50">
                  <p className="text-brand-gray-dark">
                    <strong>Owner contact email:</strong>{' '}
                    <a href="mailto:hello@ga4.so" className="text-primary hover:text-primary/80 font-semibold underline transition-colors">
                      hello@ga4.so
                    </a>
                  </p>
                </div>
              </div>
            </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-brand-gray-dark font-inter border-b border-border/50 pb-3">Types of Data Collected</h2>
            <div className="space-y-4 text-brand-gray-dark leading-relaxed">
              <p>
                Among the types of Personal Data that this Application collects, by itself or through third parties, there are: Trackers; Usage Data; first name; last name; company name; email address; billing address; geographic position; VAT Number; country; profile picture; various types of Data.
              </p>
              <p>
                Complete details on each type of Personal Data collected are provided in the dedicated sections of this privacy policy or by specific explanation texts displayed prior to the Data collection.
              </p>
              <p>
                Personal Data may be freely provided by the User, or, in case of Usage Data, collected automatically when using this Application.
              </p>
              <p>
                Unless specified otherwise, all Data requested by this Application is mandatory and failure to provide this Data may make it impossible for this Application to provide its services. In cases where this Application specifically states that some Data is not mandatory, Users are free not to communicate this Data without consequences to the availability or the functioning of the Service.
              </p>
              <p>
                Users who are uncertain about which Personal Data is mandatory are welcome to contact the Owner.
              </p>
              <p>
                Any use of Cookies – or of other tracking tools — by this Application or by the owners of third-party services used by this Application serves the purpose of providing the Service required by the User, in addition to any other purposes described in the present document.
              </p>
              <p>
                Users are responsible for any third-party Personal Data obtained, published or shared through this Application.
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-brand-gray-dark font-inter border-b border-border/50 pb-3">Mode and Place of Processing the Data</h2>
            
            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-4 text-brand-gray-dark">Methods of Processing</h3>
              <div className="space-y-4 text-brand-gray-dark leading-relaxed">
                <p>
                  The Owner takes appropriate security measures to prevent unauthorized access, disclosure, modification, or unauthorized destruction of the Data.
                </p>
                <p>
                  The Data processing is carried out using computers and/or IT enabled tools, following organizational procedures and modes strictly related to the purposes indicated. In addition to the Owner, in some cases, the Data may be accessible to certain types of persons in charge, involved with the operation of this Application (administration, sales, marketing, legal, system administration) or external parties (such as third-party technical service providers, mail carriers, hosting providers, IT companies, communications agencies) appointed, if necessary, as Data Processors by the Owner. The updated list of these parties may be requested from the Owner at any time.
                </p>
              </div>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-4 text-brand-gray-dark">Place</h3>
              <div className="space-y-4 text-brand-gray-dark leading-relaxed">
                <p>
                  The Data is processed at the Owner's operating offices and in any other places where the parties involved in the processing are located.
                </p>
                <p>
                  Depending on the User's location, data transfers may involve transferring the User's Data to a country other than their own. To find out more about the place of processing of such transferred Data, Users can check the section containing details about the processing of Personal Data.
                </p>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-bold mb-4 text-brand-gray-dark">Retention Time</h3>
              <p className="text-brand-gray-dark leading-relaxed">
                Unless specified otherwise in this document, Personal Data shall be processed and stored for as long as required by the purpose they have been collected for and may be retained for longer due to applicable legal obligation or based on the Users' consent.
              </p>
            </div>
          </section>
          <section className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-brand-gray-dark font-inter border-b border-border/50 pb-3">The Purposes of Processing</h2>
            <div className="space-y-4 text-brand-gray-dark leading-relaxed">
              <p>
                The Data concerning the User is collected to allow the Owner to provide its Service, comply with its legal obligations, respond to enforcement requests, protect its rights and interests (or those of its Users or third parties), detect any malicious or fraudulent activity, as well as the following: Analytics, Contacting the User, Displaying content from external platforms, Handling payments, Location-based interactions, Registration and authentication, Registration and authentication provided directly by this Application, Social features and Infrastructure monitoring.
              </p>
              <p>
                For specific information about the Personal Data used for each purpose, the User may refer to the section "Detailed information on the processing of Personal Data".
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-brand-gray-dark font-inter border-b border-border/50 pb-3">Detailed Information on the Processing of Personal Data</h2>
            <p className="text-brand-gray-dark mb-8 leading-relaxed">Personal Data is collected for the following purposes and using the following services:</p>

            <div className="space-y-6">
              <div>
                <h3 className="text-2xl font-bold mb-4 text-brand-gray-dark">Analytics</h3>
                <p className="text-brand-gray-dark mb-4 leading-relaxed">
                  The services contained in this section enable the Owner to monitor and analyze web traffic and can be used to keep track of User behavior.
                </p>
                <div className="p-5 border border-border/20">
                  <h4 className="font-bold text-brand-gray-dark mb-3">Google Analytics (Universal Analytics) (Google LLC)</h4>
                  <p className="text-sm text-brand-gray-dark mb-3 leading-relaxed">
                    Google Analytics (Universal Analytics) is a web analysis service provided by Google LLC or by Google Ireland Limited. Google utilizes the Data collected to track and examine the use of this Application, to prepare reports on its activities and share them with other Google services.
                  </p>
                  <p className="text-sm text-brand-gray-dark"><strong className="text-brand-gray-dark">Personal Data processed:</strong> Trackers; Usage Data.</p>
                  <p className="text-sm text-brand-gray-dark mt-2"><strong className="text-brand-gray-dark">Place of processing:</strong> United States, Ireland</p>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold mb-4 text-brand-gray-dark">Contacting the User</h3>
                <div className="p-5 border border-border/20">
                  <h4 className="font-bold text-brand-gray-dark mb-3">Contact Form</h4>
                  <p className="text-sm text-brand-gray-dark mb-3 leading-relaxed">
                    By filling in the contact form with their Data, the User authorizes this Application to use these details to reply to requests for information, quotes or any other kind of request as indicated by the form's header.
                  </p>
                  <p className="text-sm text-brand-gray-dark"><strong className="text-brand-gray-dark">Personal Data processed:</strong> company name; email address; first name; last name.</p>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold mb-4 text-brand-gray-dark">Displaying Content from External Platforms</h3>
                <p className="text-brand-gray-dark mb-4 leading-relaxed">
                  This type of service allows you to view content hosted on external platforms directly from the pages of this Application and interact with them.
                </p>
                
                <div className="space-y-3 mb-4">
                  <div className="p-5 border border-border/20">
                    <h4 className="font-bold text-brand-gray-dark mb-3">Video Vimeo (Vimeo, LLC)</h4>
                    <p className="text-sm text-brand-gray-dark mb-3 leading-relaxed">
                      Vimeo is a video content visualization service provided by Vimeo, LLC that allows this Application to incorporate content of this kind on its pages.
                    </p>
                    <p className="text-sm text-brand-gray-dark"><strong className="text-brand-gray-dark">Personal Data processed:</strong> Trackers; Usage Data.</p>
                    <p className="text-sm text-brand-gray-dark mt-2"><strong className="text-brand-gray-dark">Place of processing:</strong> United States</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold mb-4 text-brand-gray-dark">Handling Payments</h3>
                <p className="text-brand-gray-dark mb-4 leading-relaxed">
                  Unless otherwise specified, this Application processes any payments by credit card, bank transfer or other means via external payment service providers.
                </p>
                <div className="p-5 border border-border/20">
                  <p className="text-sm text-brand-gray-dark mb-3 leading-relaxed">
                    Stripe is a payment service provided by Stripe, Inc. or by Stripe Payments Europe, Limited.
                  </p>
                  <p className="text-sm text-brand-gray-dark"><strong className="text-brand-gray-dark">Personal Data processed:</strong> billing address; email address; first name; last name.</p>
                  <p className="text-sm text-brand-gray-dark mt-2"><strong className="text-brand-gray-dark">Place of processing:</strong> United States, European Union</p>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold mb-4 text-brand-gray-dark">Infrastructure Monitoring</h3>
                  <div className="p-5 border border-border/20">
                  <h4 className="font-bold text-brand-gray-dark mb-3">Sentry (Functional Software, Inc.)</h4>
                  <p className="text-sm text-brand-gray-dark mb-3 leading-relaxed">
                    Sentry is a monitoring service provided by Functional Software, Inc.
                  </p>
                  <p className="text-sm text-brand-gray-dark"><strong className="text-brand-gray-dark">Place of processing:</strong> United States</p>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold mb-4 text-brand-gray-dark">Location-based Interactions</h3>
                <p className="text-brand-gray-dark mb-4 leading-relaxed">
                  This Application may collect, use, and share User location Data in order to provide location-based services. Most browsers and devices provide tools to opt out from this feature by default.
                </p>
                <p className="text-brand-gray-dark">
                  <strong className="text-brand-gray-dark">Personal Data processed:</strong> geographic position.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold mb-4 text-brand-gray-dark">Registration and Authentication</h3>
                <p className="text-brand-gray-dark mb-4 leading-relaxed">
                  By registering or authenticating, Users allow this Application to identify them and give them access to dedicated services.
                </p>
                <div className="space-y-3 mb-4">
                  <div className="p-5 border border-border/20">
                    <h4 className="font-bold text-brand-gray-dark mb-3">Google OAuth — GA4 &amp; Search Console Access (Google LLC)</h4>
                    <p className="text-sm text-brand-gray-dark mb-3 leading-relaxed">
                      GA4.so is a native macOS desktop application. To provide its analytics features, it asks the User to sign in with Google and grant read-only access to the following Google API scopes:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-sm text-brand-gray-dark mb-3">
                      <li><code className="bg-muted px-1 rounded text-xs">https://www.googleapis.com/auth/analytics.readonly</code> — read-only access to Google Analytics 4 property data (dimensions, metrics, reports)</li>
                      <li><code className="bg-muted px-1 rounded text-xs">https://www.googleapis.com/auth/webmasters.readonly</code> — read-only access to Google Search Console performance data (clicks, impressions, CTR, position)</li>
                    </ul>
                    <p className="text-sm text-brand-gray-dark mb-3 leading-relaxed">
                      <strong>What we access:</strong> Traffic reports, session data, conversion metrics, and SEO performance data from properties the User explicitly selects. We access only the properties the User owns or has been granted access to in Google.
                    </p>
                    <p className="text-sm text-brand-gray-dark mb-3 leading-relaxed">
                      <strong>How it is used:</strong> The data is fetched directly onto the User's Mac and processed locally by the AI agent to generate reports, dashboards, and analysis. It is used solely to provide the analytics functionality the User requests.
                    </p>
                    <p className="text-sm text-brand-gray-dark mb-3 leading-relaxed">
                      <strong>Storage and sharing:</strong> Analytics data fetched via these scopes is <strong>never uploaded to, stored on, or processed by our servers</strong>. It is processed entirely on the User's local device. We do not share, sell, or transfer this data to any third party. It is not used for advertising, profiling, or any purpose beyond the specific analysis the User requests.
                    </p>
                    <p className="text-sm text-brand-gray-dark mb-3 leading-relaxed">
                      <strong>Revoking access:</strong> Users may revoke this access at any time by visiting{' '}
                      <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" className="text-primary underline">myaccount.google.com/permissions</a>{' '}
                      and removing GA4.so from the list of connected apps.
                    </p>
                    <p className="text-sm text-brand-gray-dark">
                      <strong>Place of processing:</strong> United States, Ireland (Google's OAuth infrastructure). Analytics data itself is processed locally on the User's device.
                    </p>
                  </div>

                  <div className="p-5 border border-primary/30 bg-primary/5">
                    <h4 className="font-bold text-brand-gray-dark mb-3">Google API Services — Limited Use Disclosure</h4>
                    <p className="text-sm text-brand-gray-dark leading-relaxed">
                      GA4.so's use of information received from Google APIs adheres to the{' '}
                      <a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer" className="text-primary underline">Google API Services User Data Policy</a>,
                      including the Limited Use requirements. Specifically:
                    </p>
                    <ul className="list-disc pl-6 space-y-1 text-sm text-brand-gray-dark mt-3">
                      <li>Data obtained from Google APIs is used only to provide and improve the GA4.so analytics agent features visible to the User.</li>
                      <li>We do not use Google user data to develop, improve, or train generalized AI or ML models.</li>
                      <li>We do not transfer Google user data to third parties except as necessary to provide the service (and no such transfer occurs — data stays on the User's device).</li>
                      <li>We do not use or allow humans to read Google user data unless the User has given explicit permission for a specific message, or as required for security or legal compliance.</li>
                    </ul>
                  </div>

                  <div className="p-5 border border-border/20">
                    <h4 className="font-bold text-brand-gray-dark mb-3">Direct Registration</h4>
                    <p className="text-sm text-brand-gray-dark mb-3 leading-relaxed">
                      The User registers by filling out the registration form and providing the Personal Data directly to this Application.
                    </p>
                    <p className="text-sm text-brand-gray-dark"><strong className="text-brand-gray-dark">Personal Data processed:</strong> company name; country; email address; first name; last name; profile picture; VAT Number.</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-2xl font-bold mb-4 text-brand-gray-dark">Social Features</h3>
                <p className="text-brand-gray-dark leading-relaxed">
                  This Application may use the Personal Data provided to allow Users to invite their friends and to suggest friends or connections inside it.
                </p>
              </div>

              <div>
                <h3 className="text-2xl font-bold mb-4 text-brand-gray-dark">localStorage</h3>
                <p className="text-brand-gray-dark leading-relaxed">
                  localStorage allows this Application to store and access data right in the User's browser with no expiration date.
                </p>
              </div>
            </div>
          </section>
          <section className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-brand-gray-dark font-inter border-b border-border/50 pb-3">Legal Basis of Processing</h2>
            <p className="text-brand-gray-dark mb-6 leading-relaxed">The Owner may process Personal Data relating to Users if one of the following applies:</p>
            <ul className="list-disc pl-8 space-y-3 text-brand-gray-dark leading-relaxed">
              <li>Users have given their consent for one or more specific purposes.</li>
              <li>Provision of Data is necessary for the performance of an agreement with the User and/or for any pre-contractual obligations thereof;</li>
              <li>Processing is necessary for compliance with a legal obligation to which the Owner is subject;</li>
              <li>Processing is related to a task that is carried out in the public interest or in the exercise of official authority vested in the Owner;</li>
              <li>Processing is necessary for the purposes of the legitimate interests pursued by the Owner or by a third party.</li>
            </ul>
            <p className="text-brand-gray-dark mt-6 leading-relaxed">
              In any case, the Owner will gladly help to clarify the specific legal basis that applies to the processing.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-brand-gray-dark font-inter border-b border-border/50 pb-3">Data Retention</h2>
            <p className="text-brand-gray-dark mb-6 leading-relaxed">
              Unless specified otherwise in this document, Personal Data shall be processed and stored for as long as required by the purpose they have been collected for and may be retained for longer due to applicable legal obligation or based on the Users' consent.
            </p>
            <ul className="list-disc pl-8 space-y-3 text-brand-gray-dark leading-relaxed">
              <li>Personal Data collected for purposes related to the performance of a contract between the Owner and the User shall be retained until such contract has been fully performed.</li>
              <li>Personal Data collected for the purposes of the Owner's legitimate interests shall be retained as long as needed to fulfill such purposes.</li>
              <li>The Owner may be allowed to retain Personal Data for a longer period whenever the User has given consent to such processing, as long as such consent is not withdrawn.</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-brand-gray-dark font-inter border-b border-border/50 pb-3">User Rights Under GDPR</h2>
            <p className="text-brand-gray-dark mb-6 leading-relaxed">Users may exercise certain rights regarding their Data processed by the Owner:</p>
            <ul className="list-disc pl-8 space-y-3 text-brand-gray-dark leading-relaxed">
              <li>Withdraw their consent at any time.</li>
              <li>Object to processing of their Data.</li>
              <li>Access their Data.</li>
              <li>Verify and seek rectification of their Data.</li>
              <li>Restrict the processing of their Data.</li>
              <li>Have their Personal Data deleted or otherwise removed.</li>
              <li>Receive their Data in a structured format and have it transferred to another controller.</li>
              <li>Lodge a complaint with their competent data protection authority.</li>
            </ul>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-brand-gray-dark font-inter border-b border-border/50 pb-3">How to Exercise Your Rights</h2>
            <p className="text-brand-gray-dark leading-relaxed">
              Any requests to exercise User rights can be directed to the Owner through the contact details provided in this document. Such requests are free of charge and will be answered by the Owner as early as possible and always within one month, providing Users with the information required by law.
            </p>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-brand-gray-dark font-inter border-b border-border/50 pb-3">Rights for Users in the United States</h2>
            <p className="text-brand-gray-dark mb-6 leading-relaxed">
              This part applies to all Users who are residents in the following states: California, Virginia, Colorado, Connecticut, Utah, Texas, Oregon, Nevada, Delaware, Iowa, New Hampshire, New Jersey, Nebraska, Tennessee, Minnesota, Maryland, Indiana, Kentucky, Rhode Island and Montana.
            </p>

            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-4 text-brand-gray-dark">Notice at Collection</h3>
              <p className="text-brand-gray-dark mb-4 leading-relaxed">The following categories of Personal Information may be collected:</p>
              <ul className="list-disc pl-8 space-y-2 text-brand-gray-dark">
                <li>Internet or other electronic network activity information</li>
                <li>Identifiers</li>
                <li>Commercial information</li>
                <li>Geolocation data</li>
                <li>Audio, electronic, visual, thermal, olfactory, or similar information</li>
              </ul>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-4 text-brand-gray-dark">Your Privacy Rights</h3>
              <p className="text-brand-gray-dark mb-4 leading-relaxed">You may exercise certain rights regarding your Personal Information:</p>
              <ul className="list-disc pl-8 space-y-2 text-brand-gray-dark">
                <li>The right to access Personal Information (the right to know)</li>
                <li>The right to correct inaccurate Personal Information</li>
                <li>The right to request the deletion of your Personal Information</li>
                <li>The right to obtain a copy of your Personal Information (data portability)</li>
                <li>The right to opt out of the Sale of your Personal Information</li>
                <li>The right to non-discrimination for exercising your privacy rights</li>
              </ul>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-4 text-brand-gray-dark">Additional Rights for California Residents</h3>
              <ul className="list-disc pl-8 space-y-2 text-brand-gray-dark">
                <li>The right to opt out of the Sharing of your Personal Information for cross-context behavioral advertising</li>
                <li>The right to limit our use or disclosure of your Sensitive Personal Information</li>
              </ul>
            </div>

            <div>
              <h3 className="text-2xl font-bold mb-4 text-brand-gray-dark">How to Exercise Your Privacy Rights</h3>
              <p className="text-brand-gray-dark leading-relaxed">
                To exercise the rights described above, you need to submit your request to us by contacting us via the contact details provided in this document. For us to respond to your request, we must know who you are. We will respond to your request without undue delay, but in all cases within the timeframe required by applicable law.
              </p>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-brand-gray-dark font-inter border-b border-border/50 pb-3">Additional Information</h2>

            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-4 text-brand-gray-dark">Legal Action</h3>
              <p className="text-brand-gray-dark leading-relaxed">
                The User's Personal Data may be used for legal purposes by the Owner in Court or in the stages leading to possible legal action arising from improper use of this Application or the related Services.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-4 text-brand-gray-dark">System Logs and Maintenance</h3>
              <p className="text-brand-gray-dark leading-relaxed">
                For operation and maintenance purposes, this Application and any third-party services may collect files that record interaction with this Application (System logs) or use other Personal Data (such as the IP Address) for this purpose.
              </p>
            </div>

            <div className="mb-8">
              <h3 className="text-2xl font-bold mb-4 text-brand-gray-dark">Changes to This Privacy Policy</h3>
              <p className="text-brand-gray-dark leading-relaxed">
                The Owner reserves the right to make changes to this privacy policy at any time by notifying its Users on this page and possibly within this Application. It is strongly recommended to check this page often, referring to the date of the last modification listed at the bottom.
              </p>
            </div>

            <div>
              <h3 className="text-2xl font-bold mb-4 text-brand-gray-dark">Contact Us</h3>
              <p className="text-brand-gray-dark mb-6 leading-relaxed">
                For any questions about this privacy policy or to exercise your rights, please contact us at:
              </p>
              <div className="p-8 border border-border/20">
                <p className="font-inter font-semibold text-brand-gray-dark text-lg mb-4">Gigaparsec DataLab GmbH</p>
                <p className="text-sm text-brand-gray-dark mb-4">Operating GA4.so</p>
                <p className="text-brand-gray-dark">
                  <strong className="text-brand-gray-dark">Email:</strong>{' '}
                  <a href="mailto:hello@ga4.so" className="text-primary hover:text-primary/80 font-semibold underline">
                    hello@ga4.so
                  </a>
                </p>
              </div>
            </div>
          </section>

          <section className="mt-16 pt-8 border-t border-border/50">
            <p className="text-sm text-brand-gray-dark font-inter">
              <strong className="text-brand-gray-dark">Latest update:</strong> March 07, 2026
            </p>
          </section>
        </article>
      </div>
    </main>
    </div>
  );
};

export default PrivacyPolicy;
